import { PlusIcon } from "@heroicons/react/outline";
import { BaseSyntheticEvent, useEffect, useRef, useState } from "react";

import { AutoComplete } from "../AutoCompleteInput";
import { ConfirmationModal, ConfirmationModalVariant } from "../ConfirmationModal";
import { BasicTooltip } from "../ui/Tooltip";

import { Button, DivButton } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useAllReleaseLabels, useAllWorkflowReleaseLabels } from "@/queries";
import { EntityType } from "@/queries/folder-entities/types";
import { useAllToolLabels } from "@/queries/tool-registry";
import { displayErrorToast } from "@/utils/toast";

const MAX_SUGGESTED_LABELS = 6;

interface AddReleaseLabelTriggerProps {
  isEmpty?: boolean;
  readOnly?: boolean;
  tooltipContent?: string;
  onOpenDialog: () => void;
}

export const AddReleaseLabelTrigger = ({
  isEmpty,
  readOnly,
  tooltipContent,
  onOpenDialog,
}: AddReleaseLabelTriggerProps) => (
  <BasicTooltip content={tooltipContent || "Add a new release label"}>
    <DivButton
      variant="ghost"
      size="tinyIcon"
      className={`flex h-[22px] text-xs font-normal ${
        isEmpty ? "w-auto px-1" : "w-[22px]"
      } cursor-pointer items-center justify-center rounded-sm border border-dashed border-gray-200 bg-white text-gray-600  shadow-sm outline-none transition-colors duration-200 hover:border-gray-300 hover:bg-gray-100 focus:outline-none`}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (!readOnly) {
          onOpenDialog();
        }
      }}
      disabled={readOnly}
    >
      {isEmpty ? "Release Label" : ""}
      <PlusIcon className={`h-3 w-3 ${isEmpty ? "ml-1" : ""}`} />
    </DivButton>
  </BasicTooltip>
);

interface AddReleaseLabelDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (label: string) => Promise<unknown>;
  isWorkflow?: boolean;
  entityType?: EntityType;
}

export const AddReleaseLabelDialog = ({
  isOpen,
  onOpenChange,
  onSave,
  isWorkflow = false,
  entityType,
}: AddReleaseLabelDialogProps) => {
  const userToken = useAuth()?.userToken || "";
  const { activeWorkspaceId } = useUser();
  const [label, setLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const responseRef = useRef<(() => void) | null>(null);
  const { data: promptLabels } = useAllReleaseLabels(userToken, activeWorkspaceId ?? 0);
  const { data: workflowLabels } = useAllWorkflowReleaseLabels(userToken, activeWorkspaceId ?? 0);
  const { data: toolLabels } = useAllToolLabels(
    entityType === EntityType.TOOL ? activeWorkspaceId : null,
  );
  const getLabelNames = () => {
    if (entityType === EntityType.TOOL) {
      return toolLabels?.available_tool_label_names ?? [];
    }
    if (isWorkflow || entityType === EntityType.WORKFLOW) {
      return workflowLabels?.available_workflow_label_names ?? [];
    }
    return promptLabels?.available_prompt_label_names ?? [];
  };
  const labelNames = getLabelNames();
  const labelOptions = labelNames.sort().map(name => ({
    id: name,
    value: name,
    label: name,
  }));

  const handleSave = async (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await onSave(label);
      if (typeof response === "function") {
        responseRef.current = response as () => void;
        setShowConfirmation(true);
        onOpenChange(false);
        setLabel("");
      } else {
        onOpenChange(false);
        setLabel("");
      }
    } catch {
      displayErrorToast("Failed to create release label");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setLabel("");
    }
  }, [isOpen]);

  return (
    <>
      <ConfirmationModal
        title="Move existing release label?"
        description="This label name is already assigned to another version. Release labels can only point to one version at a time, so continuing will move it here."
        isOpen={showConfirmation}
        setIsOpen={open => {
          setShowConfirmation(open);
          if (!open) {
            responseRef.current = null;
          }
        }}
        leaveButtonText="Cancel"
        headerVariant={ConfirmationModalVariant.warning}
        saveButtonText="Move label"
        useBlocker={false}
        handleOnConfirm={() => {
          responseRef.current?.();
          onOpenChange(false);
          setLabel("");
          setShowConfirmation(false);
          responseRef.current = null;
        }}
      />
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create a Release Label</DialogTitle>
          </DialogHeader>
          <div>
            <p className="mb-3 text-[0.8rem] text-gray-500">
              Release labels help identify important versions. Common examples: "prod", "staging",
              "v1".
            </p>
            <AutoComplete
              options={labelOptions}
              value={label}
              onSelect={value => setLabel(value ?? "")}
              placeholder="Enter label name (e.g., prod, staging)"
              addNewOption
              getAddOptionText={query => `Create "${query}"`}
              size="small"
            />
            {labelOptions.length > 0 && (
              <div className="mt-3">
                <p className="mb-2 text-xs font-medium text-gray-600">
                  Suggested labels
                  {labelOptions.length > MAX_SUGGESTED_LABELS && (
                    <span className="ml-1 font-normal text-gray-400">
                      ({labelOptions.length} available — type above to search)
                    </span>
                  )}
                </p>
                <div className="flex max-h-[120px] flex-wrap gap-2 overflow-y-auto">
                  {labelOptions.slice(0, MAX_SUGGESTED_LABELS).map(option => (
                    <Button
                      key={option.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setLabel(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Button type="submit" onClick={handleSave} disabled={isSaving || !label.trim()}>
                {isSaving ? "Saving..." : "Create Label"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
